import React, { useRef, useState } from 'react';

import styled, { css } from 'styled-components';
import Tooltip, { RCTooltip } from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap.css';
import { Icon } from '../Icon';
import { Button } from '../Button';

export interface MenuItem {
  action: string;
  handler: () => void;
}

export interface MoreActionsProps {
  actionItems: MenuItem[];
  placement?: string;
  trigger?: RCTooltip.Trigger[];
  color?: string;
}

export const MoreActions = ({
  actionItems = [],
  placement = 'bottomRight',
  trigger = ['click'],
  color = 'black',
}: MoreActionsProps) => {
  const container = useRef(null);

  const [visible, setVisible] = useState(false);

  const Menu = () => (
    <MenuList>
      {actionItems.map(({ action, handler }, index) => (
        <MenuListRow key={action} firstItem={index === 0}>
          <MenuItem onClick={handler}> {action} </MenuItem>
        </MenuListRow>
      ))}
    </MenuList>
  );

  return (
    <Wrapper ref={container}>
      <Ellipsis>
        <Popup
          overlay={<Menu />}
          placement={placement}
          trigger={trigger}
          destroyTooltipOnHide={false}
          getTooltipContainer={() => container.current}
          visible={visible}
          onVisibleChange={() => setVisible(!visible)}
        >
          <EllipsisContainer visible={visible}>
            <Icon
              name="verticalEllipsis"
              type="verticalEllipsis"
              size="small2"
              color={color}
            />
          </EllipsisContainer>
        </Popup>
      </Ellipsis>
    </Wrapper>
  );
};

const Popup = styled(Tooltip)`
  cursor: pointer;
`;

const Ellipsis = styled.div`
  display: inline-block;
`;

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
  .rc-tooltip {
    padding: 0;
    opacity: 1;
    .rc-tooltip-arrow {
      display: none;
    }
    .rc-tooltip-inner {
      color: ${({ theme }) => theme?.colors?.black};
      background: ${({ theme }) => theme?.colors?.white};
      border-radius: '0';
      padding: 0;
      min-height: 'auto';
      margin-top: -5px;
    }
  }
`;

const EllipsisContainer = styled.span<{ visible?: boolean }>`
  display: inline-block;
  position: relative;
  padding: 5px 0 4px;
  line-height: 0;

  ${({ visible, theme }) =>
    visible &&
    css`
      background: ${theme?.colors?.white};
      box-shadow: 0 0 4px rgb(0 0 0 / 17%);
    `}
`;

const MenuListRow = styled.li<{ firstItem?: boolean }>`
  ${({ firstItem }) =>
    !firstItem &&
    css`
      border-top: 1px solid rgba(0, 0, 0, 0.1);
    `}
`;

const MenuItem = styled(Button)`
  border-radius: 0;
  border: 2px solid ${({ theme }) => theme?.colors?.white};
  border-left: 0;
  border-right: 0;
  background: transparent;
  color: ${({ theme }) => theme?.colors?.black};
  box-shadow: none;
  :focus,
  :hover,
  :active {
    background: ${({ theme }) => theme?.colors?.selectionBlue};
    box-shadow: none;
    transform: none;
  }
`;

const MenuList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;
